// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import myw from 'myWorld-base';
import convert from 'xml-js';
import JSZip from 'jszip';
import webColorsList from './webcolors.json';

/**
 * Plugin that provides export functionality to be used on result lists<br/>
 * A button is exposed to be used in controls. It exports the app's currentFeatureSet or currentFeature
 * @name ExportFeaturesPlugin
 * @constructor
 * @extends {Plugin}
 */
myw.ExportFeaturesPlugin = myw.Plugin.extend({
    options: {
        useExternalNames: false
    },

    //ENH: simplify all of this code
    /**
     * Exports the current feature set or current feature to Json
     */
    exportToJson() {
        const formattedFeatures = {};
        let resultedFeatures;
        const features = this._getCurrentFeatures();

        formattedFeatures['type'] = 'FeatureCollection';
        formattedFeatures['features'] = [];
        resultedFeatures = formattedFeatures['features'];

        if (features[0] instanceof myw.Feature) {
            //database results

            this._sendFeatureData(features, formattedFeatures, resultedFeatures, 'json');
        } else if (features[0] instanceof myw.GeocodeFeature) {
            // geocode results

            formattedFeatures['features'] = this._formatAddresses(features);

            this._postData('json', formattedFeatures);
        }
    },

    /**
     * Exports the current feature or current feature set to CSV
     */
    exportToCsv() {
        const formattedFeatures = { results: {} };
        const resultedFeatures = formattedFeatures['results'];
        const features = this._getCurrentFeatures();
        if (features[0] instanceof myw.GeocodeFeature) {
            // geocode results

            resultedFeatures['google_addresses'] = this._formatAddresses(features);
            this._postData('csv', formattedFeatures);
        } else if (features[0] instanceof myw.Feature) {
            //database results

            this._sendFeatureData(features, formattedFeatures, resultedFeatures, 'csv');
        }
    },

    /**
     * Converts a colour string, hex or web color name to RGB
     * @return [r,g,b]   Array split on channel
     */
    colorToRGB(str) {
        if (str.indexOf('#') === -1) {
            const color = webColorsList.filter(c => c.name.toLowerCase() === str.toLowerCase())[0];
            str = color ? color.hex : '#000';
        }
        return str.replace('#', '').match(/..?/g);
    },

    generateKml() {
        const collection = this._getCurrentFeatures();
        let featureTypesStyles = {};

        collection.map(feature => {
            const type = feature.getType();
            feature.datasource.layerDefs
                .filter(layerDef => layerDef.rendering === 'vector')
                .forEach(layerDef => {
                    const [ft] = layerDef.feature_types.filter(
                        ft => ft.name === type && ft.field_name != 'annotation'
                    );
                    if (!featureTypesStyles[type] && ft) featureTypesStyles[type] = ft;
                });
        });

        return convert.js2xml(
            {
                _declaration: {
                    _attributes: {
                        version: '1.0',
                        encoding: 'utf-8'
                    }
                },
                kml: {
                    _attributes: {
                        xmlns: 'http://www.opengis.net/kml/2.2',
                        'xmlns:gx': 'http://www.google.com/kml/ext/2.2',
                        'xmlns:kml': 'http://www.opengis.net/kml/2.2',
                        'xmlns:atom': 'http://www.w3.org/2005/Atom'
                    },
                    Document: {
                        Style: Object.keys(featureTypesStyles)
                            .map(k => featureTypesStyles[k])
                            .map(style => {
                                let s = {
                                    _attributes: {
                                        id: style.name
                                    }
                                };

                                if (style.fill_style) {
                                    const [fillColor, fillOpacity] = style.fill_style.split(':');
                                    const [r, g, b] = this.colorToRGB(fillColor);
                                    const fillOpacityHex = Math.floor((fillOpacity / 100) * 255);

                                    s['PolyStyle'] = {
                                        color: `${fillOpacityHex.toString(16)}${b}${g}${r}`,
                                        colorMode: 'normal',
                                        fill: 1,
                                        outline: 1
                                    };
                                }

                                if (style.line_style) {
                                    const [lineColor, lineWidth] = style.line_style.split(':');
                                    const [r, g, b] = this.colorToRGB(lineColor);

                                    s.LineStyle = {
                                        color: `ff${b}${g}${r}`,
                                        width: lineWidth
                                    };
                                }

                                if (style.point_style) {
                                    const [shape, color] = style.point_style.split(':');
                                    const [r, g, b] = this.colorToRGB(color);

                                    if (shape.indexOf('.') >= 0) {
                                        s.IconStyle = {
                                            Icon: {
                                                href: {
                                                    _text:
                                                        'http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png'
                                                }
                                            }
                                        };
                                    } else {
                                        s.IconStyle = {
                                            color: `ff${b}${g}${r}`,
                                            Icon: {
                                                href: {
                                                    _text:
                                                        'http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png'
                                                }
                                            }
                                        };
                                    }
                                }
                                return s;
                            }),
                        Placemark: collection.map(feature => {
                            let doc = {
                                name: feature.getTitle(),
                                styleUrl: `#${feature.getType()}`,
                                ExtendedData: {
                                    Data: Object.keys(feature.properties).map(prop => ({
                                        _attributes: { name: prop },
                                        value: feature.properties[prop]
                                    }))
                                }
                            };

                            if (!feature.geometry) return doc; //ENH: Raise a warning

                            switch (feature.geometry.type) {
                                case 'LineString':
                                    doc['LineString'] = {
                                        extrude: { _text: 1 },
                                        altitudeMode: { _text: 'clampToGround' },
                                        coordinates: {
                                            _text: feature.geometry.coordinates
                                                .map(this._formatCoord)
                                                .join(' ')
                                        }
                                    };
                                    break;
                                case 'Point':
                                    doc['Point'] = {
                                        coordinates: {
                                            _text: feature.geometry.coordinates.toString()
                                        }
                                    };
                                    break;
                                case 'Polygon':
                                    doc['Polygon'] = {
                                        extrude: { _text: 1 },
                                        altitudeMode: { _text: 'clampToGround' },
                                        outerBoundaryIs: {
                                            LinearRing: {
                                                coordinates: {
                                                    _text: feature.geometry.coordinates[0]
                                                        .map(this._formatCoord)
                                                        .join(' ')
                                                }
                                            }
                                        }
                                    };
                                    break;
                                case 'MultiLineString':
                                    doc['MultiGeometry'] = {
                                        LineString: feature.geometry.coordinates.map(points => ({
                                            coordinates: points.map(this._formatCoord).join(' ')
                                        }))
                                    };
                                    break;

                                case 'MultiPoint':
                                    doc['MultiGeometry'] = {
                                        Point: feature.geometry.coordinates.map(points => ({
                                            coordinates: points.toString()
                                        }))
                                    };
                                    break;

                                case 'MultiPolygon':
                                    doc['MultiGeometry'] = {
                                        Polygon: feature.geometry.coordinates.map(group => {
                                            const outer = group[0];
                                            group.shift();
                                            const inner = group;
                                            return {
                                                extrude: { _text: 1 },
                                                altitudeMode: { _text: 'clampToGround' },
                                                outerBoundaryIs: {
                                                    LinearRing: {
                                                        coordinates: {
                                                            _text: outer
                                                                .map(this._formatCoord)
                                                                .join(' ')
                                                        }
                                                    }
                                                },
                                                innerBoundaryIs: inner.map(boundary => ({
                                                    LinearRing: {
                                                        coordinates: {
                                                            _text: boundary
                                                                .map(this._formatCoord)
                                                                .join(' ')
                                                        }
                                                    }
                                                }))
                                            };
                                        })
                                    };
                                    break;
                                default:
                                    return;
                            }
                            return doc;
                        })
                    }
                }
            },
            { ignoreComment: true, spaces: 4, compact: true }
        );
    },

    _formatCoord(c) {
        return `${c[0]},${c[1]}`;
    },

    generateDownload(content, filename, datatype) {
        const blob = new Blob([content], { type: 'octet/stream' });

        if (navigator.appVersion.toString().indexOf('.NET') > 0) {
            window.navigator.msSaveBlob(blob, filename);
        } else {
            const element = document.createElement('a');
            element.setAttribute('href', window.URL.createObjectURL(blob));
            element.setAttribute('download', filename);
            element.style.display = 'none';
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        }
    },

    generateDownloadFromUri(uri) {
        const element = document.createElement('a');
        element.setAttribute('href', uri);
        element.setAttribute('download', uri);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    },

    exportToKml() {
        const [first] = this._getCurrentFeatures();
        this.generateDownload(
            this.generateKml(),
            `${first.getType()}_export.kml`,
            'data:text/kml;charset=utf-8'
        );
    },

    exportToKmz() {
        const [first] = this._getCurrentFeatures();
        let zip = new JSZip();
        zip.file(`${first.getType()}_export.kml`, this.generateKml());
        zip.generateAsync({ type: 'blob' }).then(content => {
            this.generateDownload(
                content,
                `${first.getType()}_export.kmz`,
                'data:application/zip;base64'
            );
        });
    },

    /** have an option to export a single feature from ui
     *  If the event is triggered from featureButtons set then the currentFeature is handled
     *  otherwise the currentFeatureSet is handled
     * @return {Array<Feature>}
     * @private
     */
    _getCurrentFeatures() {
        if (this.app.currentFeature) return [this.app.currentFeature];
        else return this.app.currentFeatureSet.items;
    },

    _sendFeatureData(features, formattedFeatures, resultedFeatures, exportFormat) {
        //Make sure the features are completely loaded(have all their attributes)
        const promises = _.map(features, feature =>
            feature.ensure(['simple', 'calculated']).reflect()
        );

        // Perform the export action only when all the promises are resolved
        // Ignore the ones that are not resolved
        Promise.all(promises).then(() => {
            resultedFeatures = this._formatFeatures(exportFormat, resultedFeatures, features);
            this._postData(exportFormat, formattedFeatures);
        });
    },

    /**
     * Parses thorugh the features/address and adds the following attributes:
     * -address
     * -latitude
     * -longitude
     * @return {object} modified 'resultedFeatures'
     * @private
     */
    _formatAddresses(features) {
        const resultedFeatures = [];
        let aFeature;
        _.each(features, feature => {
            aFeature = { address: escape(feature.formattedAddress) };
            aFeature['latitude'] = feature.getLocation().lat;
            aFeature['longitude'] = feature.getLocation().lng;

            resultedFeatures.push(aFeature);
        });
        return resultedFeatures;
    },

    /**
     * Parses through the features:
     * -adds a unique id and myWorldLink to each feature
     * -removes all the feature attributes that are 'myw_' suffixed
     *
     * @return {object} modified 'resultedFeatures'
     * @private
     */
    _formatFeatures(exportFormat, resultedFeatures, features) {
        _.each(features, feature => {
            let featureObjectType,
                aFeature = {};
            if (exportFormat === 'csv') {
                // check for object type and create new list
                featureObjectType = feature.getType();
                if (!resultedFeatures.hasOwnProperty(featureObjectType)) {
                    resultedFeatures[featureObjectType] = [];
                }
                // select only the fields without myw_
                aFeature = this._formatFeature(feature);
                // add unique id for the report
                aFeature['unique id'] = feature.id;
                aFeature['myWorldLink'] = this._mapLink(feature);
                this._setAdditonalProps(aFeature, feature);
                // Add the feature to the object
                resultedFeatures[featureObjectType].push(aFeature);
            } else {
                // format data for export to JSON

                this._setAdditonalProps(aFeature, feature);
                aFeature['properties'] = this._formatFeature(feature);
                aFeature['ID'] = feature.id;
                aFeature['type'] = 'Feature';
                aFeature['geometry'] = feature.geometry;
                resultedFeatures.push(aFeature);
            }
        });
        return resultedFeatures;
    },
    /**
     * @param {object} aFeature
     * @param {Feature} feature
     * @private
     */
    _setAdditonalProps(aFeature, feature) {
        aFeature['myw_short_description'] = feature.getShortDescription();
        aFeature['myw_title'] = feature.getTitle();
    },

    _formatFeature(feature) {
        const aFeature = {};
        const fieldsOrder = feature.getFieldsOrder();
        const fieldsDD = feature.getFieldsDD();

        _.each(fieldsOrder, internalFieldName => {
            const fieldDD = fieldsDD[internalFieldName];
            const requiresUnitConversion = Boolean(
                fieldDD.display_unit && fieldDD.display_unit != fieldDD.unit
            );
            let propertyName = internalFieldName;
            let propertyValue = feature.properties[internalFieldName];

            // get external names and use it for exported file
            if (this.options.useExternalNames) {
                propertyName = fieldDD['external_name'];
            }
            // convert to display_unit if required.
            if (requiresUnitConversion) {
                propertyValue = this._applyUnitConversion(propertyValue, fieldDD);
            }

            aFeature[propertyName] = propertyValue;
        });

        return aFeature;
    },

    /**
     * Convert numeric value between units based on fieldDD settings
     * @param   {number}  value       Value to convert
     * @param   {fieldDD} fieldDD     Data dictionary information of the field to edit
     * @returns {number}              Value of the converted unit
     * @throws  {UnitNotDefinedError} Will throw if the from or to unit is not defined in the unit def
     */
    _applyUnitConversion(value, fieldDD) {
        const unit_scale = fieldDD.unit_scale;
        const fromUnit = fieldDD.unit;
        const toUnit = fieldDD.display_unit;

        const conf = myw.config['core.units'][unit_scale];
        console.log(conf);
        const unitScale = new myw.UnitScale(conf);
        return unitScale.convert(value, fromUnit, toUnit);
    },

    /**
     * Creates a map link for the passed feature
     *
     * @return: url for the feature on the map with all the visible layers
     * @private
     */
    _mapLink(feature) {
        if (!feature.getType()) return '';

        let url = this.app.getUrl();

        if (url.substring(url.length - 1) == '#') {
            url = url.substring(0, url.length - 1);
        }
        url += `?s=${encodeURIComponent(feature.getUrn())}`;

        // get turned on layers
        const currentLayers = this.app.map.getCurrentLayerIds();

        url += currentLayers.length ? `&layers=${currentLayers}` : '';

        return url;
    },

    /**
     * Ajax POST for the formattedFeatures data
     * Also adds the encoding to the request in the case of csv export
     * @private
     */
    _postData(exportFormat, formattedFeatures) {
        this.app.system.export(exportFormat, formattedFeatures).then(url => {
            this.generateDownloadFromUri(url);
        });
    }
});

const ExportButton = myw.PluginButton.extend({
    className: 'list-export',
    imgSrc: 'images/actions/export.svg',
    inactiveImgSrc: 'images/actions/export-inactive.svg',
    titleMsg: 'export',
    events: {
        mouseover: 'showMenu',
        mouseout: 'hideMenu',
        'click .export-to-json': 'exportToJson',
        'click .export-to-csv': 'exportToCsv',
        'click .export-to-kml': 'exportToKml',
        'click .export-to-kmz': 'exportToKmz'
    },
    initUI() {
        const html = `<ul class='hidden sub-list-export'>
                        <li class='export-to-csv'>CSV</li>
                        <li class='export-to-json'>JSON</li>
                        <li class='export-to-kml'>KML</li>
                        <li class='export-to-kmz'>KMZ</li>
                    </ul>`;
        this.$el.append(html);

        //remove buttons not in configuration. ENH: improve all of this
        for (const p in myw.config['core.featureDetails'].exportControls) {
            if (!myw.config['core.featureDetails'].exportControls[p]) {
                this.$(`.${p}`).remove();
            }
        }
    },

    /*
     * Sets the title of the button only when its part of a menu
     * @param {string} titleMsg Message key to retrieve the button title
     */
    setTitle: function(titleMsg) {
        if (this.mode === 'menu') {
            this.buttonTitle.html(this.owner.msg(titleMsg));
        }
    },

    showMenu() {
        if (this.$el.attr('class').indexOf('inactive') != -1) return;
        this.$('ul').show();
    },
    hideMenu() {
        this.$('ul').hide();
    },
    exportToJson() {
        this.owner.exportToJson();
    },
    exportToCsv() {
        this.owner.exportToCsv();
    },
    exportToKml() {
        this.owner.exportToKml();
    },
    exportToKmz() {
        this.owner.exportToKmz();
    }
});

myw.ExportFeaturesPlugin.prototype.buttons = {
    exportCurrentSet: ExportButton,
    exportCurrentFeature: ExportButton
};

export default myw.ExportFeaturesPlugin;
